import React, { useState } from 'react';
import { PlusIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, UsersIcon, UserPlusIcon, AlertTriangleIcon, ClockIcon, CheckCircleIcon, CalendarIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, ChevronRightIcon, FilterIcon, BellIcon, CheckIcon, XIcon } from 'lucide-react';
import { RequisitionList } from './procurement/RequisitionList';
import { PurchaseOrderList } from './procurement/PurchaseOrderList';
import { formatCurrency } from './utils/currencyFormatter';
import { Remi } from './ai/Remi';
export function Dashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for new dashboard sections
  const vendorStats = {
    pendingOnboarding: 5,
    failingCompliance: 3,
    awaitingReview: 7
  };
  const invoiceStats = {
    inProcessing: 12,
    failingCompliance: 4,
    approvedForPayment: 8
  };
  const vendorsNeedingAction = [{
    id: 'VEN-2023-004',
    name: 'Global Supply Co.',
    status: 'Incomplete',
    issue: 'Missing tax information',
    dueDate: '2023-11-15'
  }, {
    id: 'VEN-2023-005',
    name: 'Tech Solutions Inc.',
    status: 'Flagged',
    issue: 'Insurance certificate expired',
    dueDate: '2023-11-10'
  }, {
    id: 'VEN-2023-006',
    name: 'Office Essentials',
    status: 'Awaiting Review',
    issue: 'New onboarding submission',
    dueDate: '2023-11-20'
  }];
  const invoicesNeedingAttention = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-001234',
    vendor: 'Dell Technologies',
    issue: 'Unmatched PO',
    status: 'Error',
    amount: 4500.0,
    currency: 'USD',
    dueDate: '2023-11-12'
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-005678',
    vendor: 'Office Depot',
    issue: 'Missing GRN',
    status: 'Warning',
    amount: 1250.75,
    currency: 'USD',
    dueDate: '2023-11-15'
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-009012',
    vendor: 'PrintWorks Inc.',
    issue: 'Currency mismatch',
    status: 'Error',
    amount: 3200.5,
    currency: 'EUR',
    dueDate: '2023-11-18'
  }];
  const upcomingRenewals = [{
    id: 'DOC-2023-001',
    vendor: 'Dell Technologies',
    documentType: 'Insurance Certificate',
    expiryDate: '2023-11-30',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-002',
    vendor: 'Global Supply Co.',
    documentType: 'Tax Compliance',
    expiryDate: '2023-12-05',
    status: 'Expiring Soon'
  }, {
    id: 'DOC-2023-003',
    vendor: 'Tech Solutions Inc.',
    documentType: 'Business License',
    expiryDate: '2023-12-15',
    status: 'Expiring Soon'
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-3679">
      <div className="mb-8 flex justify-between items-center" data-id="element-3680">
        <div data-id="element-3681">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3682">
            P2P Control Centre
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3683">
            Comprehensive overview of procurement, vendor management, and
            accounts payable
          </p>
        </div>
        <div className="flex items-center" data-id="element-3684">
          <div className="flex items-center" data-id="element-3685">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-3686" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-3687">
              <option value="7days" data-id="element-3688">Last 7 days</option>
              <option value="30days" data-id="element-3689">Last 30 days</option>
              <option value="90days" data-id="element-3690">Last 90 days</option>
              <option value="ytd" data-id="element-3691">Year to date</option>
            </select>
          </div>
        </div>
      </div>
      {/* Remi AI Assistant */}
      <div className="mb-8" data-id="element-3692">
        <Remi setCurrentView={setCurrentView} data-id="element-3693" />
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-3694">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3695">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3696">
          {/* Existing cards */}
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3697">
            <div className="p-5" data-id="element-3698">
              <div className="flex items-center" data-id="element-3699">
                <div className="flex-shrink-0" data-id="element-3700">
                  <FileTextIcon className="h-6 w-6 text-gray-400" data-id="element-3701" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3702">
                  <dl data-id="element-3703">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3704">
                      Total Requisitions
                    </dt>
                    <dd data-id="element-3705">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3706">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-3707">
            <div className="p-5" data-id="element-3708">
              <div className="flex items-center" data-id="element-3709">
                <div className="flex-shrink-0" data-id="element-3710">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-3711" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3712">
                  <dl data-id="element-3713">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3714">
                      Pending Approval
                    </dt>
                    <dd data-id="element-3715">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3716">
                        {pendingApproval}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-3717">
            <div className="p-5" data-id="element-3718">
              <div className="flex items-center" data-id="element-3719">
                <div className="flex-shrink-0" data-id="element-3720">
                  <ClipboardCheckIcon className="h-6 w-6 text-indigo-400" data-id="element-3721" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3722">
                  <dl data-id="element-3723">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3724">
                      Purchase Orders
                    </dt>
                    <dd data-id="element-3725">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3726">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-3727">
            <div className="p-5" data-id="element-3728">
              <div className="flex items-center" data-id="element-3729">
                <div className="flex-shrink-0" data-id="element-3730">
                  <TrendingUpIcon className="h-6 w-6 text-green-400" data-id="element-3731" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3732">
                  <dl data-id="element-3733">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3734">
                      Total Spend
                    </dt>
                    <dd data-id="element-3735">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3736">
                        {formatCurrency(totalReqSpend + totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Vendor Status Summary Cards */}
      <div className="mb-8" data-id="element-3737">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3738">
          Vendor Management
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3739">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3740">
            <div className="p-5" data-id="element-3741">
              <div className="flex items-center" data-id="element-3742">
                <div className="flex-shrink-0" data-id="element-3743">
                  <UserPlusIcon className="h-6 w-6 text-yellow-400" data-id="element-3744" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3745">
                  <dl data-id="element-3746">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3747">
                      Vendors Pending Onboarding
                    </dt>
                    <dd data-id="element-3748">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3749">
                        {vendorStats.pendingOnboarding}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('compliance-check')} data-id="element-3750">
            <div className="p-5" data-id="element-3751">
              <div className="flex items-center" data-id="element-3752">
                <div className="flex-shrink-0" data-id="element-3753">
                  <AlertTriangleIcon className="h-6 w-6 text-orange-400" data-id="element-3754" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3755">
                  <dl data-id="element-3756">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3757">
                      <span className="group relative" data-id="element-3758">
                        Vendors Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3759">
                          Includes expired insurance, missing tax details, etc.
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3760">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3761">
                        {vendorStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('vendor-management')} data-id="element-3762">
            <div className="p-5" data-id="element-3763">
              <div className="flex items-center" data-id="element-3764">
                <div className="flex-shrink-0" data-id="element-3765">
                  <ClockIcon className="h-6 w-6 text-blue-400" data-id="element-3766" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3767">
                  <dl data-id="element-3768">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3769">
                      Vendors Awaiting Review
                    </dt>
                    <dd data-id="element-3770">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3771">
                        {vendorStats.awaitingReview}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoice Summary Cards */}
      <div className="mb-8" data-id="element-3772">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3773">
          Accounts Payable
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3774">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3775">
            <div className="p-5" data-id="element-3776">
              <div className="flex items-center" data-id="element-3777">
                <div className="flex-shrink-0" data-id="element-3778">
                  <ReceiptIcon className="h-6 w-6 text-purple-400" data-id="element-3779" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3780">
                  <dl data-id="element-3781">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3782">
                      Invoices in Processing
                    </dt>
                    <dd data-id="element-3783">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3784">
                        {invoiceStats.inProcessing}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('matching')} data-id="element-3785">
            <div className="p-5" data-id="element-3786">
              <div className="flex items-center" data-id="element-3787">
                <div className="flex-shrink-0" data-id="element-3788">
                  <AlertCircleIcon className="h-6 w-6 text-red-400" data-id="element-3789" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3790">
                  <dl data-id="element-3791">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3792">
                      <span className="group relative" data-id="element-3793">
                        Invoices Failing Compliance
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-3794">
                          Unmatched PO, blocked vendor, tax or value mismatches
                        </span>
                      </span>
                    </dt>
                    <dd data-id="element-3795">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3796">
                        {invoiceStats.failingCompliance}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('invoice-list')} data-id="element-3797">
            <div className="p-5" data-id="element-3798">
              <div className="flex items-center" data-id="element-3799">
                <div className="flex-shrink-0" data-id="element-3800">
                  <CreditCardIcon className="h-6 w-6 text-green-400" data-id="element-3801" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-3802">
                  <dl data-id="element-3803">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3804">
                      Invoices Approved for Payment
                    </dt>
                    <dd data-id="element-3805">
                      <div className="text-lg font-medium text-gray-900" data-id="element-3806">
                        {invoiceStats.approvedForPayment}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3807">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3808">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3809">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3810">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3811">
              View all
            </button>
          </div>
          <RequisitionList requisitions={recentRequisitions} locale={locale} data-id="element-3812" />
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3813">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3814">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3815">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3816">
              View all
            </button>
          </div>
          <PurchaseOrderList purchaseOrders={recentPurchaseOrders} locale={locale} data-id="element-3817" />
        </div>
      </div>
      {/* New Dashboard Sections */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-3818">
        {/* Vendors Needing Action */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3819">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3820">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3821">
              Vendors Needing Action
            </h3>
            <button onClick={() => setCurrentView('vendor-management')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3822">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3823">
            {vendorsNeedingAction.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3824">
                No vendors need attention at this time.
              </li> : vendorsNeedingAction.map(vendor => <li key={vendor.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('vendor-management')} data-id="element-3825">
                  <div className="flex items-center justify-between" data-id="element-3826">
                    <div className="flex items-center" data-id="element-3827">
                      <div className="flex-shrink-0" data-id="element-3828">
                        {vendor.status === 'Incomplete' ? <XIcon className="h-5 w-5 text-red-400" data-id="element-3829" /> : vendor.status === 'Flagged' ? <AlertTriangleIcon className="h-5 w-5 text-orange-400" data-id="element-3830" /> : <ClockIcon className="h-5 w-5 text-blue-400" data-id="element-3831" />}
                      </div>
                      <div className="ml-3" data-id="element-3832">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3833">
                          {vendor.name}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-3834">{vendor.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-3835">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-3836">
                        Due: {vendor.dueDate}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('vendor-management');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3837">
                        Review
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
        {/* Invoices Needing Attention */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-3838">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3839">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3840">
              Invoices Needing Attention
            </h3>
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3841">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3842">
            {invoicesNeedingAttention.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3843">
                No invoices need attention at this time.
              </li> : invoicesNeedingAttention.map(invoice => <li key={invoice.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('invoice-list')} data-id="element-3844">
                  <div className="flex items-center justify-between" data-id="element-3845">
                    <div className="flex items-center" data-id="element-3846">
                      <div className="flex-shrink-0" data-id="element-3847">
                        {invoice.status === 'Error' ? <AlertCircleIcon className="h-5 w-5 text-red-400" data-id="element-3848" /> : <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-3849" />}
                      </div>
                      <div className="ml-3" data-id="element-3850">
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3851">
                          {invoice.invoiceNumber} - {invoice.vendor}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-3852">{invoice.issue}</p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-3853">
                      <span className="text-sm text-gray-500 mr-4" data-id="element-3854">
                        {formatCurrency(invoice.amount, invoice.currency, locale)}
                      </span>
                      <button onClick={e => {
                  e.stopPropagation();
                  setCurrentView('matching');
                }} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-3855">
                        Resolve
                      </button>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>
      </div>
      {/* Upcoming Renewals / Expiries */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8" data-id="element-3856">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3857">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3858">
            Upcoming Renewals & Expiries
          </h3>
          <button onClick={() => setCurrentView('compliance-check')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-3859">
            View all
          </button>
        </div>
        <ul className="divide-y divide-gray-200" data-id="element-3860">
          {upcomingRenewals.length === 0 ? <li className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3861">
              No upcoming renewals or expiries.
            </li> : upcomingRenewals.map(renewal => <li key={renewal.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('compliance-check')} data-id="element-3862">
                <div className="flex items-center justify-between" data-id="element-3863">
                  <div className="flex items-center" data-id="element-3864">
                    <div className="flex-shrink-0" data-id="element-3865">
                      <CalendarIcon className="h-5 w-5 text-orange-400" data-id="element-3866" />
                    </div>
                    <div className="ml-3" data-id="element-3867">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-3868">
                        {renewal.vendor} - {renewal.documentType}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3869">
                        Expires on {renewal.expiryDate}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-3870">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-3871">
                      <ClockIcon className="mr-1 h-3 w-3" data-id="element-3872" />
                      {renewal.status}
                    </span>
                  </div>
                </div>
              </li>)}
        </ul>
      </div>
      {/* Optional: Vendor Compliance Trend Chart */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md mb-8 p-5" data-id="element-3873">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3874">
          Vendor Compliance Trend (Last 3 Months)
        </h3>
        <div className="h-64 flex items-center justify-center" data-id="element-3875">
          <div className="w-full h-full flex items-end justify-around px-10" data-id="element-3876">
            <div className="flex flex-col items-center" data-id="element-3877">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '70%'
            }} data-id="element-3878"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3879">Sep</p>
              <p className="text-xs text-gray-400" data-id="element-3880">85%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-3881">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '82%'
            }} data-id="element-3882"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3883">Oct</p>
              <p className="text-xs text-gray-400" data-id="element-3884">92%</p>
            </div>
            <div className="flex flex-col items-center" data-id="element-3885">
              <div className="w-16 bg-green-500 rounded-t" style={{
              height: '78%'
            }} data-id="element-3886"></div>
              <p className="mt-2 text-sm text-gray-500" data-id="element-3887">Nov</p>
              <p className="text-xs text-gray-400" data-id="element-3888">89%</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}